import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth, api } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  CalendarCheck2, LogOut, LogIn, ReceiptText, MessageSquare, Sun,
  BedDouble, ShieldCheck, KeyRound, BadgeCheck, Scale, Plus, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { VisitorCommentsDialog } from "@/components/VisitorCommentsDialog";

const Row = ({ item, right, onComment }) => (
  <div className="flex items-center gap-2 text-sm py-1.5 border-b border-[#F1F1EE] last:border-0">
    <span className="font-medium text-[#1C1F1E] truncate">{item.visitor_name}</span>
    {item.visitor_no && <span className="text-[10px] text-[#7A8A84] shrink-0">{item.visitor_no}</span>}
    <span className="text-xs text-[#7A8A84] shrink-0">{item.stay_from}→{item.stay_to}</span>
    <span className="ml-auto flex items-center gap-1.5 shrink-0">
      {right}
      <button onClick={() => onComment(item)} className="text-[#7A8A84] hover:text-[#1B4332]"
              title="Visitor comments" data-testid={`ac-comment-${item.booking_id}`}>
        <MessageSquare className="h-3.5 w-3.5" />
        {item.comment_count > 0 && <span className="text-[9px]">{item.comment_count}</span>}
      </button>
    </span>
  </div>
);

const Section = ({ icon: Icon, title, items, empty, render, testid }) => (
  <div data-testid={testid}>
    <div className="text-xs font-semibold uppercase tracking-wider text-[#525C58] flex items-center gap-1.5 mb-1">
      <Icon className="h-3.5 w-3.5 text-[#1B4332]" /> {title}
      <Badge className="bg-[#F1F1EE] text-[#525C58] border-0 text-[10px]">{items.length}</Badge>
    </div>
    {items.length === 0
      ? <p className="text-xs text-[#B9C0BD] mb-2">{empty}</p>
      : <div className="mb-2">{items.map(render)}</div>}
  </div>
);

export function ActionCenter() {
  const { hasPerm } = useAuth();
  const [data, setData] = useState(null);
  const [commentFor, setCommentFor] = useState(null);
  const [reconcileInv, setReconcileInv] = useState(null); // {invoice, visitor_name}
  const [actingId, setActingId] = useState(null);
  const canSee = hasPerm("bookings.update");

  const load = useCallback(() => {
    api.get("/housing/action-center").then(({ data }) => setData(data)).catch(() => {});
  }, []);
  useEffect(() => { if (canSee) load(); }, [canSee, load]);

  const checkIn = async (item) => {
    setActingId(item.booking_id);
    try {
      await api.post(`/bookings/${item.booking_id}/check-in`);
      toast.success(`${item.visitor_name} checked in`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Check-in failed"); }
    finally { setActingId(null); }
  };
  const checkOut = async (item) => {
    setActingId(item.booking_id);
    try {
      await api.post(`/bookings/${item.booking_id}/check-out`);
      toast.success(`${item.visitor_name} checked out`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Check-out failed"); }
    finally { setActingId(null); }
  };
  const checkInBtn = (i) => i.status === "confirmed" && (
    <Button size="sm" onClick={() => checkIn(i)} disabled={actingId === i.booking_id}
            className="h-6 px-2 text-[10px] bg-[#1B4332] hover:bg-[#2D6A4F]" data-testid={`ac-checkin-${i.booking_id}`}>
      <LogIn className="h-3 w-3 mr-0.5" /> Check in
    </Button>
  );
  const checkOutBtn = (i) => i.status === "checked_in" && (
    <Button size="sm" variant="outline" onClick={() => checkOut(i)} disabled={actingId === i.booking_id}
            className="h-6 px-2 text-[10px] border-blue-300 text-blue-800" data-testid={`ac-checkout-${i.booking_id}`}>
      <LogOut className="h-3 w-3 mr-0.5" /> Check out
    </Button>
  );

  if (!canSee || !data) return null;

  const invBadge = (item) => item.invoice
    ? <Badge className={`text-[9px] border ${item.invoice.status === "paid" ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-amber-50 text-amber-900 border-amber-300"}`}>#{item.invoice.number} {item.invoice.status}</Badge>
    : <Badge className="text-[9px] bg-red-50 text-red-800 border border-red-200">no invoice</Badge>;

  return (
    <Card className="border-[#E8E8E6]" data-testid="action-center-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Sun className="h-4 w-4 text-[#1B4332]" /> Today &amp; this week
          <span className="text-xs font-normal text-[#7A8A84]">— what needs doing</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-2">
        <div>
          <Section icon={LogIn} title="Arriving today" items={data.arrivals_today} empty="No arrivals today" testid="ac-arrivals-today"
                   render={(i) => <Row key={i.booking_id} item={i} onComment={setCommentFor} right={checkInBtn(i)} />} />
          <Section icon={LogOut} title="Departing today" items={data.departures_today} empty="No departures today" testid="ac-departures-today"
                   render={(i) => <Row key={i.booking_id} item={i} onComment={setCommentFor}
                                       right={<span className="flex items-center gap-1">{invBadge(i)}{checkOutBtn(i)}</span>} />} />
          <Section icon={BedDouble} title="Currently checked in" items={data.checked_in} empty="Nobody checked in" testid="ac-checked-in"
                   render={(i) => <Row key={i.booking_id} item={i} onComment={setCommentFor} right={checkOutBtn(i)} />} />
          <Section icon={CalendarCheck2} title="Arriving this week" items={data.arrivals_week} empty="Quiet week ahead" testid="ac-arrivals-week"
                   render={(i) => <Row key={i.booking_id} item={i} onComment={setCommentFor} />} />
        </div>
        <div>
          <Section icon={ReceiptText} title="Invoices to sort out" items={data.invoice_todo}
                   empty="All departures invoiced & paid" testid="ac-invoice-todo"
                   render={(i) => (
                     <Row key={i.booking_id} item={i} onComment={setCommentFor} right={
                       <span className="flex items-center gap-1">
                         {invBadge(i)}
                         {i.invoice && (
                           <Button size="sm" variant="outline" className="h-6 px-2 text-[10px] border-[#1B4332]/30 text-[#1B4332]"
                                   onClick={() => setReconcileInv({ invoiceId: i.invoice.id, visitor: i.visitor_name })}
                                   data-testid={`ac-reconcile-${i.booking_id}`}>
                             <Scale className="h-3 w-3 mr-0.5" /> Reconcile
                           </Button>
                         )}
                         <Link to="/invoices" className="text-[10px] text-[#1B4332] underline">open</Link>
                       </span>
                     } />
                   )} />
          <Section icon={BadgeCheck} title="Booking changes to acknowledge" items={data.pending_revisions}
                   empty="No unacknowledged changes" testid="ac-pending-revisions"
                   render={(i) => <Row key={i.booking_id} item={i} onComment={setCommentFor}
                                       right={<Link to="/bookings" className="text-[10px] text-amber-700 underline">review</Link>} />} />
          <Section icon={BedDouble} title="Unhoused (next 14 days)" items={data.unhoused_upcoming}
                   empty="Everyone has a room" testid="ac-unhoused"
                   render={(i) => <Row key={i.booking_id} item={i} onComment={setCommentFor}
                                       right={<Link to="/scramble" className="text-[10px] text-amber-700 underline">scramble</Link>} />} />
          <Section icon={ShieldCheck} title="Licenses in progress (arrivals ≤30d)" items={data.licenses_open}
                   empty="No open license work" testid="ac-licenses"
                   render={(i) => <Row key={i.booking_id} item={i} onComment={setCommentFor}
                                       right={<Link to="/medical-licenses" className="text-[10px] text-[#1B4332] underline">track</Link>} />} />
          <Section icon={KeyRound} title="Leases starting soon" items={data.leases_starting_soon}
                   empty="No leases starting this week" testid="ac-leases"
                   render={(l) => (
                     <div key={l.id} className="flex items-center gap-2 text-sm py-1.5 border-b border-[#F1F1EE] last:border-0">
                       <span className="font-medium">{l.tenant_name}</span>
                       <span className="text-xs text-[#7A8A84]">{l.unit_name || ""} · starts {l.start_date}</span>
                       <Link to="/leases" className="ml-auto text-[10px] text-[#1B4332] underline">open</Link>
                     </div>
                   )} />
        </div>
      </CardContent>
      <VisitorCommentsDialog bookingId={commentFor?.booking_id} visitorName={commentFor?.visitor_name}
                             onClose={() => setCommentFor(null)} onSaved={load} />
      <ReconcileDialog data={reconcileInv} onClose={() => setReconcileInv(null)} onSaved={load} />
    </Card>
  );
}

function ReconcileDialog({ data, onClose, onSaved }) {
  const [adjustments, setAdjustments] = useState([]);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!data) return;
    api.get(`/invoices/${data.invoiceId}`).then(({ data: inv }) => {
      setAdjustments((inv.adjustments || []).map((a) => ({ ...a })));
      setNotes(inv.reconcile_notes || "");
    }).catch(() => {});
  }, [data]);

  if (!data) return null;

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/invoices/${data.invoiceId}/reconcile`, { adjustments, notes });
      toast.success("Invoice reconciled");
      onClose();
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to reconcile");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!data} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="reconcile-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Scale className="h-5 w-5 text-[#1B4332]" /> Reconcile invoice — {data.visitor}</DialogTitle>
          <DialogDescription>Add end-of-visit adjustments (extra nights, damages, discounts — use negative amounts for credits).</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {adjustments.map((a, i) => (
            <div key={i} className="flex items-end gap-2" data-testid={`adjustment-row-${i}`}>
              <div className="flex-1 space-y-1"><Label className="text-xs">Description</Label>
                <Input className="h-9" value={a.description || ""} onChange={(e) => setAdjustments((p) => p.map((x, xi) => xi === i ? { ...x, description: e.target.value } : x))} /></div>
              <div className="w-24 space-y-1"><Label className="text-xs">Amount</Label>
                <Input className="h-9" type="number" step="0.01" value={a.amount ?? ""} onChange={(e) => setAdjustments((p) => p.map((x, xi) => xi === i ? { ...x, amount: e.target.value } : x))} /></div>
              <div className="w-20 space-y-1"><Label className="text-xs">Cur</Label>
                <Input className="h-9" value={a.currency || "KES"} onChange={(e) => setAdjustments((p) => p.map((x, xi) => xi === i ? { ...x, currency: e.target.value.toUpperCase() } : x))} /></div>
              <Button variant="ghost" size="sm" className="text-red-600 h-9" onClick={() => setAdjustments((p) => p.filter((_, xi) => xi !== i))}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => setAdjustments((p) => [...p, { description: "", amount: 0, currency: "KES" }])} data-testid="add-adjustment-btn">
            <Plus className="h-4 w-4 mr-1" /> Add adjustment
          </Button>
          <div className="space-y-1"><Label className="text-xs">Reconciliation notes</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="reconcile-notes" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#1B4332] hover:bg-[#2D6A4F]" data-testid="reconcile-save-btn">
            {saving ? "Saving…" : "Save reconciliation"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
